import { create } from 'zustand';

import type { CompanyLink } from '../lib/companyLinks';

/**
 * « Mes sociétés » of the signed-in person (lot BS): the single front copy of their links, read by the account menus
 * and the profile through `useMyCompanyLinks`. Keyed by the account it was read for, so that another account signing in
 * on the same tab never sees the previous one's links.
 *
 * `idle`: never asked. `error.message` is for the screen, in French.
 */
export type CompanyLinksState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading'; readonly userId: string }
  | { readonly status: 'ready'; readonly userId: string; readonly links: readonly CompanyLink[] }
  | { readonly status: 'error'; readonly userId: string; readonly message: string };

interface CompanyLinksStore {
  readonly state: CompanyLinksState;
  readonly startLoading: (userId: string) => void;
  readonly setLinks: (userId: string, links: readonly CompanyLink[]) => void;
  readonly setError: (userId: string, message: string) => void;
  readonly reset: () => void;
}

const IDLE: CompanyLinksState = { status: 'idle' };

/** State and pure setters only; the reads and writes live in `useMyCompanyLinks` (ENGINEERING.md § 5). An answer for
 *  an account that is no longer the one being read is dropped. */
export const useCompanyLinksStore = create<CompanyLinksStore>()((set) => ({
  state: IDLE,
  startLoading: (userId) => set({ state: { status: 'loading', userId } }),
  setLinks: (userId, links) =>
    set((s) => (s.state.status !== 'idle' && s.state.status !== 'error' && s.state.userId === userId
      ? { state: { status: 'ready', userId, links } }
      : s)),
  setError: (userId, message) =>
    set((s) => (s.state.status === 'loading' && s.state.userId === userId ? { state: { status: 'error', userId, message } } : s)),
  reset: () => set({ state: IDLE }),
}));
